/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React from 'react';
import {SelectItemShape} from '@dvrd/dvr-controls';
import EmailStep from "../models/emailStepModel";
import {CustomerType, EmailFlow, Fulfilment, UnlayerDesign} from "./interfaces";
import EmailTemplate from "../models/emailTemplateModel";
import Account from "../models/accountModel";
import InfoIcon from "../components/icon/infoIcon";

export type TemplateParams = {name: string; subject: string; json: UnlayerDesign};

function genDayItems(step: EmailStep): SelectItemShape[] {
    const daysLabel = step.onlyWorkDays ? 'werkdag' : 'dag';
    const {flow} = step;
    let sevenDays: SelectItemShape;
    if (flow === EmailFlow.REVIEW_INVOICE)
        sevenDays = {
            value: 7,
            label: <label className='recommend-days'>7 {daysLabel}en na zending <span style={{height: '28px'}}
                                                                                      title='Voor beste conversie, verzend je Review + Factuur mail minimaal 7 dagen na zending. Dan heeft je klant de bestelling waarschijnlijk ontvangen en kunnen testen.'><InfoIcon/></span></label>,
            valueLabel: `7 ${daysLabel}en na zending`
        };
    else sevenDays = {value: 7, label: `7 ${daysLabel}en na zending`};
    return [
        {value: 0, label: `Order verzonden`},
        {value: 1, label: `1 ${daysLabel} na zending`},
        {value: 3, label: `3 ${daysLabel}en na zending`},
        sevenDays,
        {value: 14, label: `14 ${daysLabel}en na zending`},
    ];
}

export function getDayItems(step: EmailStep, account: Account | null): SelectItemShape[] {
    const items = genDayItems(step).slice();
    if (!step.withInvoice || account?.getFlex('override_email_limitations'))
        items.splice(0, 0, {value: -1, label: 'Order ontvangen', selectable: false});
    let daysLabel = step.onlyWorkDays ? 'werkdag' : 'dag';
    if (step.days > 1) daysLabel += 'en';
    return items.concat([{value: 'custom', label: 'Aangepast', valueLabel: `${step.days} ${daysLabel} na zending`}]);
}

export const customerTypeItems: SelectItemShape[] = [
    {value: CustomerType.BUSINESS, label: "Zakelijke klanten"},
    {value: CustomerType.PRIVATE, label: "Particuliere klanten"},
    {value: CustomerType.BOTH, label: "Alle klanten"},
];

export function getStepTitle(step: EmailStep): string {
    switch (step.flow) {
        case EmailFlow.INVOICE:
            return 'FACTUUR MAIL';
        case EmailFlow.OTHER:
            return 'ANDERE MAIL';
        case EmailFlow.PRODUCT:
            return 'PRODUCT MAIL';
        case EmailFlow.REVIEW:
            return 'REVIEW MAIL';
        case EmailFlow.REVIEW_INVOICE:
            return 'REVIEW + FACTUUR MAIL';
        case EmailFlow.CREDIT_INVOICE:
            return 'MAIL MET CREDITFACTUUR';
    }
}

export function getTemplateLabel(step: EmailStep, templates: EmailTemplate[]): string {
    return templates.find(template => template.id === step.templateId)?.name ?? 'Kies template';
}

export function getProductsLabel(step: EmailStep, productsCount: number): string {
    const {productEans} = step;
    if (!productEans.length) return 'Kies product(en)';
    else if (productEans.includes('all')) {
        if (productEans.length === 1)
            return 'Alle producten';
        else if (productsCount === -1) return 'Producten laden...';
        const count = productsCount - (productEans.length - 1);
        return `${count} producten`;
    } else if (productEans.includes('remaining')) return 'Resterende producten';
    else if (productEans.length === 1) return productEans[0];
    else return `${productEans.length} producten`;
}

export function getAttachmentsLabel(step: EmailStep): string {
    const {attachments, withInvoice} = step;
    const totalAttachments = attachments.length + (withInvoice ? 1 : 0);
    if (!totalAttachments) return 'Geen';
    else if (totalAttachments === 1) {
        if (withInvoice) return step.flow === EmailFlow.CREDIT_INVOICE ? 'Creditfactuur.pdf' : 'Factuur.pdf';
        else return attachments[0].name;
    } else return `${totalAttachments} bijlages`;
}

export function getCustomersLabel(step: EmailStep): string {
    if (step.customerType === CustomerType.BUSINESS) return 'Zakelijke klanten';
    if (step.customerType === CustomerType.PRIVATE) return 'Particuliere klanten';
    return 'Alle klanten';
}

export function getCountriesLabel(step: EmailStep): string {
    const count = step.countries.length;
    if (!count) return 'Alle landen';
    if (count === 1) return step.countries[0];
    return `${count} landen`;
}

export function getLogisticsLabel(step: EmailStep): string {
    if (step.fulfilment === Fulfilment.FBB) return 'LVB';
    if (step.fulfilment === Fulfilment.FBR) return 'Eigen logistiek';
    return 'Alle logistiek';
}

export type FlowParams = {title: string; description: string;}

export function getFlowParams(flow: EmailFlow): FlowParams | null {
    switch (flow) {
        case EmailFlow.INVOICE:
            return {title: 'Factuur mail', description: 'Een mail met de BTW factuur als bijlage'};
        case EmailFlow.REVIEW_INVOICE:
            return {
                title: 'Review + Factuur mail (aanbevolen)',
                description: 'Vraag om een productbeoordeling inclusief link en voeg de factuur toe als bijlage.'
            };
        case EmailFlow.REVIEW:
            return {title: 'Review mail', description: 'Vraag om een productreview op Bol.'};
        case EmailFlow.PRODUCT:
            return {
                title: 'Productniveau mail',
                description: 'Verstuur een mail alleen bij bepaalde bestelde producten. Bijvoorbeeld een product handleiding mail met bijlage.'
            };
        case EmailFlow.OTHER:
            return {title: 'Andere mail', description: 'Stel zelf een ander soort mail samen.'};
    }
    return null;
}